import { useEffect, useState } from "react";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { trackEvent } from "@/lib/analytics";
import { formatRelative } from "@/lib/dateFormat";

export interface SavedLookCardProps {
  outfitId: string;
  issueNumber: number;
  imageUrl: string | null;
  heroName: string;
  heroRetailer: string | null;
  createdAt: string;
  onTap: () => void;
  onUnsave: () => void;
}

export function SavedLookCard(props: SavedLookCardProps) {
  const {
    outfitId,
    issueNumber,
    imageUrl,
    heroName,
    heroRetailer,
    createdAt,
    onTap,
    onUnsave,
  } = props;
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    setImgLoaded(false);
  }, [imageUrl]);

  const issueLabel =
    issueNumber < 10 ? `No. 0${issueNumber}` : `No. ${issueNumber}`;
  const meta = heroRetailer
    ? `${heroRetailer} · ${formatRelative(createdAt)}`
    : formatRelative(createdAt);

  return (
    <button
      type="button"
      onClick={() => {
        void triggerHaptic("tab-switch");
        trackEvent("saved_card_tapped", { outfitId, issueNumber });
        onTap();
      }}
      className="text-left"
      style={{
        background: "#FFFCF8",
        border: "0.5px solid #E8E2D9",
        borderRadius: 16,
        overflow: "hidden",
        cursor: "pointer",
        padding: 0,
        width: "100%",
        display: "block",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "3 / 4",
          background:
            "linear-gradient(135deg, #F1ECE3 0%, #E8E0D5 100%)",
        }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt={heroName}
            onLoad={() => setImgLoaded(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: imgLoaded ? 1 : 0,
              transition: "opacity 200ms ease",
            }}
          />
        )}
        <span
          role="button"
          aria-label="Remove from saved"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            void triggerHaptic("selection");
            trackEvent("saved_card_unsaved", { outfitId });
            onUnsave();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              void triggerHaptic("selection");
              trackEvent("saved_card_unsaved", { outfitId });
              onUnsave();
            }
          }}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 44,
            height: 44,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 26,
              height: 26,
              borderRadius: 999,
              background: "rgba(255,255,255,0.9)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="ti ti-bookmark-filled"
              style={{ fontSize: 12, color: "#C84B2F" }}
              aria-hidden="true"
            />
          </span>
        </span>
        <span
          style={{
            position: "absolute",
            bottom: 8,
            left: 8,
            background: "rgba(44,40,32,0.85)",
            color: "#F5F0EB",
            borderRadius: 10,
            padding: "2px 7px",
            fontSize: 9,
            letterSpacing: "0.04em",
          }}
        >
          {issueLabel}
        </span>
      </div>
      <div style={{ padding: "8px 10px 10px" }}>
        <div
          style={{
            fontFamily: "var(--font-display, serif)",
            fontStyle: "italic",
            fontSize: 12,
            color: "#2C2820",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {heroName}
        </div>
        <div
          className="l2f-type-caption"
          style={{
            marginTop: 2,
            color: "#9B958E",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {meta}
        </div>
      </div>
    </button>
  );
}
